import { PAISES_TXT } from './paises-datos';
import { sinTildes } from './texto';
import type { Moneda } from './dinero';

export interface Pais { iso: string; nombre: string; dial: string; moneda: Moneda; lens: number[] }
const MONEDA_PAIS: Record<string, Moneda> = { CO: 'COP', DO: 'DOP' };
export const PAISES: Pais[] = PAISES_TXT.split(';').map(r => {
  const [iso, nombre, dial, l] = r.split('|');
  return { iso, nombre, dial, moneda: MONEDA_PAIS[iso] || 'USD', lens: l ? l.split(',').map(Number) : [] };
});
export const PAIS: Record<string, Pais> = Object.fromEntries(PAISES.map(p => [p.iso, p]));
// País principal de cada indicativo compartido (+1 = Estados Unidos, +44 = Reino Unido…).
export const PRINCIPAL: Record<string, string> = { '1': 'US', '7': 'RU', '39': 'IT', '44': 'GB', '47': 'NO', '61': 'AU', '212': 'MA', '262': 'RE', '358': 'FI', '590': 'GP', '599': 'CW' };
// Los países de siempre van primero en la lista; el resto, en orden alfabético.
export const FRECUENTES = ['CO', 'US', 'DO', 'PR', 'CA', 'MX', 'PA', 'CR', 'EC', 'PE', 'VE', 'CL', 'AR', 'ES'];
export const PAISES_LISTA: Pais[] = [...FRECUENTES.map(i => PAIS[i]).filter(Boolean), ...PAISES.filter(p => !FRECUENTES.includes(p.iso))];
export const paisLabel = (p?: Pais | null) => (p ? `${p.nombre} (+${p.dial})` : '');

export function buscarPais(txt: string): Pais | null {
  const t = sinTildes(txt).trim();
  if (!t) return null;
  const exacto = PAISES.find(p => sinTildes(paisLabel(p)) === t || sinTildes(p.nombre) === t);
  if (exacto) return exacto;
  const pref = PAISES.filter(p => sinTildes(p.nombre).startsWith(t));
  if (pref.length === 1) return pref[0];
  if (t.startsWith('+')) {
    const dial = t.replace(/\D/g, ''), m = PAISES.filter(p => p.dial === dial);
    if (m.length === 1) return m[0];
    if (m.length > 1) return PAIS[PRINCIPAL[dial]] || m[0];
  }
  return null;
}

// Número local (sin indicativo) a partir de lo que se escribió: quita el indicativo y el 0 inicial de marcación nacional.
export function localDe(pais: Pais | null, txt: string): string {
  let d = String(txt || '').replace(/\D/g, '');
  if (pais && d.startsWith(pais.dial) && (String(txt).trim().startsWith('+') || d.length > (pais.dial === '57' || pais.dial === '1' ? 10 : 12)
      || (pais.lens.length && !pais.lens.includes(d.length) && pais.lens.includes(d.length - pais.dial.length)))) d = d.slice(pais.dial.length);
  if (pais && pais.iso !== 'CO' && d.startsWith('0') && pais.lens.length && !pais.lens.includes(d.length) && pais.lens.includes(d.length - 1)) d = d.slice(1);
  return d;
}

// WhatsApp del cliente: '' si está bien; si no, el mensaje de error.
export function validarNumero(pais: Pais, local: string): string {
  if (!local) return 'Falta el número de WhatsApp.';
  if (pais.iso === 'CO') return /^3\d{9}$/.test(local) ? '' : 'Un celular de Colombia tiene 10 dígitos y empieza por 3.';
  if (pais.dial === '1') {
    if (!/^\d{10}$/.test(local)) return 'Un número +1 tiene 10 dígitos: código de área y número.';
    const rd = ['809', '829', '849'].includes(local.slice(0, 3));
    if (pais.iso === 'DO' && !rd) return 'Los números de República Dominicana empiezan por 809, 829 u 849.';
    if (pais.iso !== 'DO' && rd) return 'Ese código de área es de República Dominicana: cambia el país.';
    return '';
  }
  if (pais.lens.length && !pais.lens.includes(local.length)) {
    const l = pais.lens;
    return `Un número de ${pais.nombre} tiene ${l.length > 1 ? l.slice(0, -1).join(', ') + ' u ' + l[l.length - 1] : l[0]} dígitos, sin el indicativo.`;
  }
  return /^\d{6,14}$/.test(local) ? '' : 'Revisa el número: debe tener entre 6 y 14 dígitos.';
}

// Teléfono de quien recibe: celular o fijo (en Colombia, 10 dígitos que empiezan por 3 o por 60).
export function validarTelRecibe(pais: Pais, local: string): string {
  if (!local) return 'Falta el teléfono de quien recibe.';
  if (pais.iso === 'CO') return /^(3\d{9}|60\d{8})$/.test(local) ? '' : 'Un teléfono de Colombia tiene 10 dígitos: celular (empieza por 3) o fijo (empieza por 60).';
  return validarNumero(pais, local);
}

export const agrupar = (d: string) => (d.length === 10 ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}` : d.replace(/(\d{3})(?=\d)/g, '$1 '));
// "+57 300 555 0001" a partir del país y el número completo con indicativo (E.164 sin "+").
export function telefonoTexto(iso: string | null | undefined, e164: string): string {
  const p = iso ? PAIS[iso] : null;
  return p && e164.startsWith(p.dial) ? `+${p.dial} ${agrupar(e164.slice(p.dial.length))}` : (e164 ? '+' + e164 : '');
}
